<template>
    <div ref="startTable" :style="{'scroll-margin-top': headerHeight}">
        <form class="card p-3 mb-3" @submit.prevent="applySearch">
            <div class="row g-3">
                <div class="col-md-3">
                    <label class="form-label">Name</label>
                    <input
                        v-model="options.search"
                        class="form-control"
                        placeholder="Type Name..."
                    />
                </div>

                <div class="col-md-3 d-flex align-items-end gap-2">
                    <button type="submit" class="btn btn-primary py-1 px-5" :disabled="isLoading">
                        <i class="fas fa-search" title="Search"></i>
                    </button>
                    <button type="button" class="btn btn-secondary py-1 ml-3 px-5" :disabled="isLoading" @click="clearSearch">
                        <i class="fas fa-times" title="Clear"></i>
                    </button>
                </div>
            </div>
        </form>

        <div class="table-responsive mt-3">
            <table class="table table-bordered table-striped udm-table">
                <thead>
                <tr>
                    <th>Name</th>
                    <th v-for="column in columns" :key="column.id">
                        {{ column.label }}
                    </th>
                    <th style="width: 60px"></th>
                </tr>
                </thead>

                <tbody>
                <tr
                    v-for="row in records"
                    :key="row.id"
                    class="udm-row-click"
                    @click="goToRecord(row)"
                >
                    <td>{{ row.name || '—' }}</td>
                    <td v-for="column in columns" :key="`${row.id}-${column.id}`">
                        {{ row.values[column.id] || '—' }}
                    </td>
                    <td class="udm-eye-cell">
                        <i class="fas fa-eye" title="Show"></i>
                    </td>
                </tr>

                <tr v-if="!isLoading && records.length === 0">
                    <td :colspan="columns.length + 2" class="text-center fw-bold py-3">
                        No Records Found
                    </td>
                </tr>

                <tr v-if="isLoading">
                    <td :colspan="columns.length + 2" class="text-center py-3">
                        Loading...
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <Pagination
            :options.sync="options"
            :total-items="total"
            @change="loadData({scroll: true})"
        />
    </div>
</template>

<script>
import Pagination from "./public/Pagination.vue";
import {headerOffset} from "../mixins/headerOffset";

export default {
    name: "LookupTableComponent",
    components: {Pagination},
    mixins: [headerOffset],
    props: {
        lookupId: {
            type: Number,
            required: true,
        },
        columns: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            restoring: true,
            isLoading: false,
            records: [],
            total: 0,
            _debounceTimer: null,
            options: {
                page: 1,
                itemsPerPage: 10,
                search: '',
                detail_ids: this.columns.map(column => column.id),
            },
        };
    },
    computed: {
        queryOptionsNamespace() {
            return `lookup_${this.lookupId}`;
        },
    },
    watch: {
        "options.search"() {
            if (this.restoring) return;

            clearTimeout(this._debounceTimer);
            this._debounceTimer = setTimeout(() => {
                this.options.page = 1;
                this.loadData();
            }, 1000);
        },
    },
    async mounted() {
        this.$query.bind(this, {
            namespace: this.queryOptionsNamespace,
        });

        await this.$nextTick();
        this.restoring = false;
        await this.loadData();
    },
    beforeDestroy() {
        clearTimeout(this._debounceTimer);
    },
    methods: {
        async loadData({scroll = false} = {}) {
            this.isLoading = true;

            try {
                const {data} = await this.$api.get(
                    `api/v1/udm/lookups/${this.lookupId}/items`,
                    {
                        params: {
                            ...this.options,
                            search: this.options.search || '',
                        },
                    },
                );

                this.records = (data.data || []).map(row => ({
                    ...row,
                    values: (row.detail_values || []).reduce((values, detail) => ({
                        ...values,
                        [detail.lookup_table_detail_id]: detail.value,
                    }), {}),
                }));
                this.total = data.meta?.total || 0;
            } finally {
                this.isLoading = false;

                if (scroll) {
                    this.$nextTick(() => this.$refs.startTable?.scrollIntoView({behavior: "smooth"}));
                }
            }
        },
        goToRecord(row) {
            window.location.href = `/udm/lookup-items/${row.id}`;
        },
        applySearch() {
            this.applySearchNow(this.options.search);
        },
        clearSearch() {
            this.applySearchNow('');
        },
        applySearchNow(search) {
            clearTimeout(this._debounceTimer);
            this.options.search = search.trim();
            this.options.page = 1;
            this.loadData();

            this.$nextTick(() => {
                clearTimeout(this._debounceTimer);
            });
        },
    },
};
</script>

<style scoped>
.table-responsive {
    background: #f6f6f6;
}

.udm-table {
    table-layout: fixed;
    width: 100%;
    min-width: 600px;
}

.udm-table thead {
    border-bottom: 3px solid #c6c6c6;
}

.udm-table th {
    font-weight: 600;
}

.udm-table th,
.udm-table td {
    overflow: hidden;
    overflow-wrap: anywhere;
}

.udm-table td {
    vertical-align: middle;
    text-align: left;
}

.udm-row-click {
    cursor: pointer;
}

.udm-eye-cell {
    text-align: center !important;
}

.fa-eye {
    color: #0b100b;
    cursor: pointer;
}

.fa-eye:hover {
    color: #0f192a;
}
</style>
